import React, { useState, useEffect } from 'react';
import { Bell, Moon, Sun, Search, ChevronDown, User, Settings, LogOut, Monitor } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { useTheme } from '../ui/ThemeProvider';
import Badge from '../ui/Badge';

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const { state, logout } = useAuth();
  const { user } = state;
  const { theme, setTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mounted, setMounted] = useState(false);

  // After mounting, we can safely show the UI that depends on the theme
  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    if (showNotifications) setShowNotifications(false);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showUserMenu) setShowUserMenu(false);
  };

  const handleThemeChange = () => {
    if (theme === 'dark') {
      setTheme('light');
    } else if (theme === 'light') {
      setTheme('dark');
    } else {
      setTheme('dark');
    }
  };

  if (!mounted) {
    return <div className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"></div>;
  }

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 flex items-center px-6 sticky top-0 z-30">
      <div className="flex-1 flex items-center">
        <div className="relative w-64 max-w-xs hidden md:block">
          <input
            type="text"
            placeholder="Search..."
            className="w-full h-9 pl-10 pr-4 rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-900 focus:ring-0 text-sm"
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <button
          onClick={handleThemeChange}
          className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun size={20} />
          ) : theme === 'light' ? (
            <Moon size={20} />
          ) : (
            <Monitor size={20} />
          )}
        </button>
        
        <div className="relative">
          <button
            onClick={toggleNotifications}
            className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
            aria-label="Notifications"
          >
            <Bell size={20} />
            <span className="absolute top-0 right-0 h-4 w-4 bg-emerald-500 rounded-full text-xs text-white flex items-center justify-center">
              3
            </span>
          </button>
          
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
              </div>
              <div className="max-h-60 overflow-y-auto">
                <a href="#" className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-emerald-100 dark:bg-emerald-800 rounded-full p-1">
                      <Bell size={16} className="text-emerald-600 dark:text-emerald-300" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">New transaction detected</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Your credit card was charged $45.00</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">2 hours ago</p>
                    </div>
                  </div>
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-green-100 dark:bg-green-800 rounded-full p-1">
                      <Bell size={16} className="text-green-600 dark:text-green-300" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Budget goal reached</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">You've reached your savings goal for this month!</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">1 day ago</p>
                    </div>
                  </div>
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-yellow-100 dark:bg-yellow-800 rounded-full p-1">
                      <Bell size={16} className="text-yellow-600 dark:text-yellow-300" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Unusual spending detected</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Your spending on Entertainment is 30% higher than usual</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">2 days ago</p>
                    </div>
                  </div>
                </a>
              </div>
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                <a href="#" className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">View all notifications</a>
              </div>
            </div>
          )}
        </div>
        
        <div className="relative">
          <button
            onClick={toggleUserMenu}
            className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
          >
            <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-medium">
              {user?.name.charAt(0).toUpperCase()}
            </div>
            <span className="ml-2 hidden md:block font-medium text-gray-700 dark:text-gray-300">{user?.name}</span>
            <ChevronDown size={16} className="ml-1 hidden md:block text-gray-500 dark:text-gray-400" />
          </button>
          
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
              </div>
              <Link to="/settings" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <div className="flex items-center">
                  <User size={16} className="mr-2" />
                  Profile
                </div>
              </Link>
              <Link to="/settings" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <div className="flex items-center">
                  <Settings size={16} className="mr-2" />
                  Settings
                </div>
              </Link>
              <button 
                onClick={logout}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center">
                  <LogOut size={16} className="mr-2" />
                  Logout
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;